// =============================================================================
// FILE: stores/hoverIntelligence.store.ts
// PURPOSE: R2 Smart Hover Intelligence — user settings & action dispatch
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type HoverElementType =
  | 'document'
  | 'email'
  | 'event'
  | 'drawing'
  | 'code'
  | 'text'
  | 'project'
  | 'asset'
  | 'contact';

export interface HoverAction {
  id: string;
  label: string;
  icon: string;
  color: string;
  shortcut?: string;
  requiresAI?: boolean;
}

export const HOVER_ACTIONS: Record<HoverElementType, HoverAction[]> = {
  document: [
    { id: 'ai-summary',        label: 'AI Summary',         icon: 'Sparkles',     color: '#a78bfa', requiresAI: true },
    { id: 'explain',           label: 'Explain',            icon: 'HelpCircle',   color: '#60a5fa', requiresAI: true },
    { id: 'version-history',   label: 'Version History',    icon: 'GitBranch',    color: '#4ade80' },
    { id: 'related-docs',      label: 'Related Documents',  icon: 'Link2',        color: '#5eead4' },
    { id: 'copy-ref',          label: 'Copy Reference',     icon: 'Copy',         color: '#94a3b8', shortcut: 'C' },
    { id: 'create-task',       label: 'Create Task',        icon: 'PlusCircle',   color: '#f59e0b' },
    { id: 'bookmark',          label: 'Bookmark',           icon: 'Bookmark',     color: '#ec4899' },
    { id: 'open-location',     label: 'Open Location',      icon: 'FolderOpen',   color: '#34d399' },
  ],
  email: [
    { id: 'summarize',         label: 'Summarize',          icon: 'AlignLeft',    color: '#a78bfa', requiresAI: true },
    { id: 'draft-reply',       label: 'Draft Reply',        icon: 'Reply',        color: '#60a5fa', requiresAI: true },
    { id: 'extract-actions',   label: 'Extract Actions',    icon: 'ListChecks',   color: '#4ade80', requiresAI: true },
    { id: 'schedule-followup', label: 'Schedule Follow-up', icon: 'CalendarPlus', color: '#f59e0b' },
    { id: 'find-attachments',  label: 'Find Attachments',   icon: 'Paperclip',    color: '#5eead4' },
    { id: 'link-project',      label: 'Link to Project',    icon: 'Link',         color: '#34d399' },
  ],
  event: [
    { id: 'prepare',           label: 'Prepare Meeting',    icon: 'CheckSquare',  color: '#5eead4', requiresAI: true },
    { id: 'generate-agenda',   label: 'Generate Agenda',    icon: 'List',         color: '#a78bfa', requiresAI: true },
    { id: 'collect-docs',      label: 'Collect Documents',  icon: 'FolderCheck',  color: '#60a5fa' },
    { id: 'open-decisions',    label: 'Open Decisions',     icon: 'GitFork',      color: '#f87171' },
    { id: 'countdown',         label: 'Countdown',          icon: 'Clock',        color: '#f59e0b' },
    { id: 'meeting-intel',     label: 'Meeting Intelligence', icon: 'Brain',      color: '#ec4899', requiresAI: true },
  ],
  drawing: [
    { id: 'zoom',              label: 'Zoom',               icon: 'ZoomIn',       color: '#5eead4', shortcut: 'Z' },
    { id: 'layer-info',        label: 'Layer Info',         icon: 'Layers',       color: '#60a5fa' },
    { id: 'equipment-details', label: 'Equipment Details',  icon: 'Package',      color: '#4ade80' },
    { id: 'cross-ref',         label: 'Cross References',   icon: 'Link2',        color: '#94a3b8' },
    { id: 'ai-explain',        label: 'AI Explain',         icon: 'Sparkles',     color: '#a78bfa', requiresAI: true },
    { id: 'open-sheets',       label: 'Open Related Sheets', icon: 'FileStack',   color: '#f59e0b' },
  ],
  code: [
    { id: 'explain-code',      label: 'Explain Code',       icon: 'HelpCircle',   color: '#60a5fa', requiresAI: true },
    { id: 'optimize',          label: 'Optimize',           icon: 'Zap',          color: '#f59e0b', requiresAI: true },
    { id: 'find-errors',       label: 'Find Errors',        icon: 'AlertTriangle', color: '#f87171', requiresAI: true },
    { id: 'generate-docs',     label: 'Generate Docs',      icon: 'FileText',     color: '#a78bfa', requiresAI: true },
  ],
  text: [
    { id: 'explain',           label: 'Explain',            icon: 'HelpCircle',   color: '#60a5fa', requiresAI: true },
    { id: 'copy',              label: 'Copy',               icon: 'Copy',         color: '#5eead4', shortcut: 'C' },
    { id: 'create-note',       label: 'Create Note',        icon: 'PenLine',      color: '#4ade80' },
    { id: 'ai-expand',         label: 'AI Expand',          icon: 'Sparkles',     color: '#a78bfa', requiresAI: true },
    { id: 'translate',         label: 'Translate',          icon: 'Languages',    color: '#f59e0b', requiresAI: true },
  ],
  project: [
    { id: 'open-project',      label: 'Open Project',       icon: 'FolderOpen',   color: '#5eead4', shortcut: 'O' },
    { id: 'ai-status',         label: 'AI Status Brief',    icon: 'Sparkles',     color: '#a78bfa', requiresAI: true },
    { id: 'create-task',       label: 'Create Task',        icon: 'PlusCircle',   color: '#f59e0b' },
    { id: 'team-status',       label: 'Team Status',        icon: 'Users',        color: '#60a5fa' },
    { id: 'timeline',          label: 'Timeline',           icon: 'Activity',     color: '#4ade80' },
  ],
  asset: [
    { id: 'view-specs',        label: 'View Specs',         icon: 'FileText',     color: '#5eead4' },
    { id: 'maintenance-log',   label: 'Maintenance Log',    icon: 'Wrench',       color: '#f59e0b' },
    { id: 'location',          label: 'Location',           icon: 'MapPin',       color: '#60a5fa' },
    { id: 'ai-explain',        label: 'AI Explain',         icon: 'Sparkles',     color: '#a78bfa', requiresAI: true },
  ],
  contact: [
    { id: 'contact-profile',   label: 'Profile',            icon: 'User',         color: '#5eead4' },
    { id: 'draft-email',       label: 'Draft Email',        icon: 'Mail',         color: '#60a5fa', requiresAI: true },
    { id: 'meeting-history',   label: 'Meeting History',    icon: 'Clock',        color: '#4ade80' },
    { id: 'link-project',      label: 'Link to Project',    icon: 'Link',         color: '#f59e0b' },
  ],
};

export const ELEMENT_TYPE_LABELS: Record<HoverElementType, string> = {
  document: 'Document',
  email:    'Email',
  event:    'Calendar Event',
  drawing:  'Drawing',
  code:     'Code',
  text:     'Text',
  project:  'Project',
  asset:    'Asset',
  contact:  'Contact',
};

interface HoverIntelligenceState {
  // Settings
  enabled: boolean;
  delay: number; // ms
  enabledTypes: HoverElementType[];
  maxActions: number;
  showAIBadge: boolean;

  // Actions
  setEnabled: (v: boolean) => void;
  setDelay: (ms: number) => void;
  toggleType: (type: HoverElementType) => void;
  setMaxActions: (n: number) => void;
  setShowAIBadge: (v: boolean) => void;
}

export const useHoverIntelligence = create<HoverIntelligenceState>()(
  persist(
    (set, get) => ({
      enabled: true,
      delay: 800,
      enabledTypes: ['document', 'email', 'event', 'drawing', 'text', 'project', 'asset', 'contact'],
      maxActions: 6,
      showAIBadge: true,

      setEnabled: (v) => set({ enabled: v }),
      setDelay: (ms) => set({ delay: Math.max(200, Math.min(3000, ms)) }),
      toggleType: (type) =>
        set((s) => ({
          enabledTypes: s.enabledTypes.includes(type)
            ? s.enabledTypes.filter((t) => t !== type)
            : [...s.enabledTypes, type],
        })),
      setMaxActions: (n) => set({ maxActions: Math.max(2, Math.min(8, n)) }),
      setShowAIBadge: (v) => set({ showAIBadge: v }),
    }),
    { name: 'r2-hover-intelligence' },
  ),
);
